import {
  getAuth,
  signInAnonymously,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCustomToken,
  signInWithCredential,
  linkWithPopup
} from 'firebase/auth';
import { API_URL } from '../constants/string-constants';

export const switchToFirebaseUser = async (targetUserId) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    // If we're already the target user, no need to switch
    if (currentUser && currentUser.uid === targetUserId) {
      return currentUser;
    }
    
    // Sign out current user and sign in as the target user
    // For anonymous users, we'll need to sign in anonymously and hope we get the same UID
    // This is a limitation of Firebase - we can't directly switch to a specific anonymous user
    
    // Store current state before switching
    const wasSignedIn = !!currentUser;
    
    if (currentUser) {
      await auth.signOut();
    }
    
    // Sign in anonymously - Firebase will create a new anonymous user
    // We can't force it to be a specific UID, so we'll need to handle this differently
    const userCredential = await signInAnonymously(auth);
    
    // Check if we got the expected user ID
    if (userCredential.user.uid === targetUserId) {
      console.log(`Successfully switched to target Firebase user: ${targetUserId}`);
      return userCredential.user;
    } else {
      console.log(`Signed in as new anonymous user: ${userCredential.user.uid}, expected: ${targetUserId}`);
      // We got a different user ID than expected
      // This is expected behavior with Firebase anonymous auth
      return userCredential.user;
    }
    
  } catch (error) {
    console.error('Error switching Firebase user:', error);
    throw error;
  }
};

export const signInWithAccountNumber = async (accountNumber) => {
  try {
    // First, ensure we have a Firebase user
    const auth = getAuth();
    let currentUser = auth.currentUser;
    
    if (!currentUser) {
      const userCredential = await signInAnonymously(auth);
      currentUser = userCredential.user;
    }
    
    // The backend will handle the account number verification
    // We don't need to switch users since the account number becomes associated
    // with whatever Firebase user makes the request
    
    return currentUser;
  } catch (error) {
    console.error('Error signing in with account number:', error);
    throw error;
  }
};

export const signInWithGoogle = async () => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const provider = new GoogleAuthProvider();

    // Only request email scope, skip profile info
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    provider.addScope('email');

    let linkedUser;
    let googleId;
    let googleEmail;

    try {
      // Use linkWithPopup to add Google credentials to the existing anonymous user
      // This keeps the same Firebase UID instead of creating a new user
      console.log('Linking Google account to current user:', currentUser?.uid);
      const result = await linkWithPopup(currentUser, provider);
      linkedUser = result.user;
      googleId = result.user.providerData.find(p => p.providerId === 'google.com')?.uid;
      googleEmail = result.user.email;
      console.log('Google account linked to Firebase user:', linkedUser.uid);
    } catch (linkError) {
      if (linkError.code === 'auth/credential-already-in-use') {
        // This Google account is already linked to a different Firebase user
        // We need to sign in as that user instead
        console.log('Google account already linked to another Firebase user, switching...');

        // Get the Google credential from the error - no need for another popup
        const credential = GoogleAuthProvider.credentialFromError(linkError);

        if (credential) {
          // Sign in with the credential from the failed link attempt (no second popup needed)
          const result = await signInWithCredential(auth, credential);
          linkedUser = result.user;
          googleId = result.user.providerData.find(p => p.providerId === 'google.com')?.uid;
          googleEmail = result.user.email;
          console.log('Signed in with existing Google Firebase user:', linkedUser.uid);

          // Call backend to get custom token for the VentScape user with this Google account
          const signinResponse = await fetch(`${API_URL}auth/google/signin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ google_id: googleId })
          });

          if (signinResponse.ok) {
            const signinData = await signinResponse.json();
            // Switch to the existing VentScape user
            await signInWithCustomToken(auth, signinData.custom_token);
            console.log('Switched to existing VentScape user:', signinData.user_id);
            return auth.currentUser;
          }
          // Don't fall through — let the /link endpoint handle it below
          // (it will detect the existing user by email and return switch_required)
          console.warn('Google signin endpoint failed, falling through to link endpoint');
        } else {
          console.error('Could not get credential from linkWithPopup error');
          throw linkError;
        }
      } else {
        throw linkError;
      }
    }

    // Get fresh ID token for the linked user
    const idToken = await auth.currentUser.getIdToken(true);
    console.log('Got user token for Google linking');

    // Link Google account to VentScape backend
    const response = await fetch(`${API_URL}auth/google/link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        google_id: googleId,
        email: googleEmail
      })
    });

    if (!response.ok) {
      throw new Error(`Google link request failed: ${response.status}`);
    }

    const data = await response.json();

    if (data.switch_required) {
      console.log('Google account already exists in VentScape, switching to original user:', data.user_id);
      // This Google account belongs to a different VentScape user - switch to that user
      await signInWithCustomToken(auth, data.custom_token);
      return auth.currentUser;
    }

    console.log('Google account linked successfully to current user');
    return auth.currentUser;

  } catch (error) {
    console.error('Google sign-in failed:', error);
    throw error;
  }
};